// 批次上傳圖片工具(移植自國中 chaiwriting-j utils/imageUtils.js + batch-upload.js parseFilename)

// 壓縮上傳圖片(長邊上限、只縮不放),降低上傳量與 canvas 負擔
export const compressImageForUpload = (
  file: File,
  maxLongSide = 1280,
  quality = 0.92
): Promise<{ file: File; previewUrl: string }> => {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const { width, height } = img;
      const longSide = Math.max(width, height);

      if (longSide <= maxLongSide) {
        resolve({ file, previewUrl: URL.createObjectURL(file) });
        return;
      }

      const ratio = maxLongSide / longSide;
      const newW = Math.round(width * ratio);
      const newH = Math.round(height * ratio);

      const canvas = document.createElement("canvas");
      canvas.width = newW;
      canvas.height = newH;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve({ file, previewUrl: URL.createObjectURL(file) });
        return;
      }
      ctx.drawImage(img, 0, 0, newW, newH);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve({ file, previewUrl: URL.createObjectURL(file) });
            return;
          }
          const compressedFile = new File([blob], file.name, {
            type: "image/jpeg",
          });
          resolve({
            file: compressedFile,
            previewUrl: URL.createObjectURL(compressedFile),
          });
        },
        "image/jpeg",
        quality
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ file, previewUrl: URL.createObjectURL(file) });
    };
    img.src = url;
  });
};

export interface ParsedFilename {
  seatNumber: string; // 純數字字串,無法解析時為 ""
  name: string;
  pageNumber: number | null; // 檔名尾 _N / -N(1~3)= 稿紙面別
}

/* 檔名解析(與國中一致):
   1.jpg        → { seatNumber: "1", name: "" }
   1_小明.jpg   → { seatNumber: "1", name: "小明" }
   1_小明_2.jpg → { seatNumber: "1", name: "小明", pageNumber: 2 }
   A_小明_1.jpg → { seatNumber: "",  name: "小明", pageNumber: 1 }
   IMG_001.jpg  → { seatNumber: "",  name: "" }                     */
export function parseFilename(filename: string): ParsedFilename {
  const base = filename.replace(/\.[^.]+$/, "");

  let pageNumber: number | null = null;
  const pageMatch = base.match(/[_-](\d+)$/);
  if (pageMatch) {
    const n = parseInt(pageMatch[1], 10);
    if (n >= 1 && n <= 3) pageNumber = n;
  }

  const sepMatch = base.match(/^([^_ -]+)[_ -](.+)$/);

  if (sepMatch) {
    const firstPart = sepMatch[1];
    const rest = sepMatch[2];

    const seatNumber = /^\d+$/.test(firstPart)
      ? String(parseInt(firstPart, 10))
      : "";

    const name = rest.replace(/[_ -]\d+$/, "").trim();
    const validName = /^\d+$/.test(name) ? "" : name;

    return { seatNumber, name: validName, pageNumber };
  }

  if (/^\d+$/.test(base)) {
    return {
      seatNumber: String(parseInt(base, 10)),
      name: "",
      pageNumber: null,
    };
  }

  return { seatNumber: "", name: "", pageNumber: null };
}

/**
 * 一張待標註圖片的座號/姓名初值:老師自己命名的圖片照 parseFilename,
 * PDF 展開出來的頁一律不吃檔名。
 *
 * 為什麼 PDF 要例外(kanban 108142):一份 PDF 裝的是**全班**,那個檔名不代表
 * 任何一位學生。但 expandFilesWithPdf 會把頁命名成「原檔名_頁碼.jpg」送進
 * parseFilename,硬拆出來的殘渣就佔住了 seatNumber / name —— 而 BatchUpload
 * 判斷「要不要用 AI 辨識結果」看的正是這兩欄是否為空,於是辨識跑完卻被整包丟棄。
 * 實測:class_scan_13p.pdf 上傳後姓名欄全是 "scan_13p"、座號 10 張全空要手填;
 * 更糟的 9-19班作文.pdf 會拆出座號 "9",因為欄位有值連「座號必填」都擋不住,
 * 整班會併到 9 號名下且畫面看不出異常。
 *
 * 頁碼一併清成 null,不要沿用頁檔名尾碼:parseFilename 只認 1~3(見上方
 * pageMatch),所以一份 PDF 的第 4 頁之後全是 null。單一 PDF 時「1,2,3,null…」
 * 剛好隨索引單調不減,buildGroups 的排序等於沒作用;但一個班超過 5 人(正反面)
 * 就得分多份 PDF 上傳,此時第二份的第 1 頁拿到 pn=1、第一份的第 10 頁是 null
 * (排序用 ?? 99),同一位學生的兩面會被倒置。清成 null 讓排序退回索引順序
 * = 上傳順序 = PDF 頁順序,永遠正確。
 */
export function resolveInitialAnnotation(
  filename: string,
  fromPdf: boolean
): ParsedFilename {
  const parsed = parseFilename(filename);
  if (!fromPdf) return parsed;
  return { seatNumber: "", name: "", pageNumber: null };
}

export function truncateFilename(name: string, maxLen = 24): string {
  if (name.length <= maxLen) return name;
  return `${name.slice(0, maxLen - 6)}…${name.slice(-5)}`;
}
